/**
 * Utilities for field overview organization and processing
 */

import { getFieldId, parseFieldId, normalizeValidationErrorFieldId } from './fieldIdUtils';

/**
 * Check if a field has a value (not null, undefined, or empty string)
 */
export const hasFieldValue = (value) => {
  return value !== null && value !== undefined && value !== '';
};

/**
 * Check if a field has a corresponding input element in the DOM
 * Optimized with caching to avoid repeated DOM queries
 */
const inputElementCache = new Map();
let cacheTimestamp = 0;
const CACHE_TTL = 1000; // Cache for 1 second

export const hasInputElement = (fieldId) => {
  if (typeof document === 'undefined') return false;
  
  // Clear cache if it's too old
  const now = Date.now();
  if (now - cacheTimestamp > CACHE_TTL) {
    inputElementCache.clear();
    cacheTimestamp = now;
  }
  
  // Check cache first
  if (inputElementCache.has(fieldId)) {
    return inputElementCache.get(fieldId);
  }
  
  // Query DOM and cache result
  const exists = document.getElementById(fieldId) !== null;
  inputElementCache.set(fieldId, exists);
  return exists;
};

/**
 * Clear the input element cache (useful when DOM changes significantly)
 */
export const clearInputElementCache = () => {
  inputElementCache.clear();
  cacheTimestamp = 0;
};

/**
 * Generate all required field IDs based on data structure
 */
export const getAllRequiredFieldIds = (extractedData) => {
  const fieldIds = new Set();
  
  if (!extractedData) return fieldIds;
  
  const declarations = Array.isArray(extractedData) ? extractedData : [extractedData];
  
  declarations.forEach((declaration, declarationIndex) => {
    // Declaration required fields
    fieldIds.add(`expediente_${declarationIndex + 1}`);
    fieldIds.add(`tipo_${declarationIndex + 1}`);
    fieldIds.add(`clave_declarante_${declarationIndex + 1}`);
    fieldIds.add(`contacto_telefono_${declarationIndex + 1}`);
    fieldIds.add(`flag_datos_seguridad_${declarationIndex + 1}`);
    
    // Item required fields
    declaration.partidas?.forEach((item, itemIndex) => {
      fieldIds.add(`descripcion_mercancia_${declarationIndex + 1}_${itemIndex}`);
      fieldIds.add(`codigo_nc_${declarationIndex + 1}_${itemIndex}`);
      fieldIds.add(`masa_bruta_${declarationIndex + 1}_${itemIndex}`);
      fieldIds.add(`masa_neta_${declarationIndex + 1}_${itemIndex}`);
      fieldIds.add(`pais_origen_${declarationIndex + 1}_${itemIndex}`);
      
      // Package required fields
      item.embalajes?.forEach((embalaje, embalajeIndex) => {
        fieldIds.add(`packages_tipo_${declarationIndex + 1}_${itemIndex}_${embalajeIndex}`);
        fieldIds.add(`packages_es_interno_o_externo_${declarationIndex + 1}_${itemIndex}_${embalajeIndex}`);
        fieldIds.add(`packages_cantidad_${declarationIndex + 1}_${itemIndex}_${embalajeIndex}`);
        
        embalaje.marcas?.forEach((marca, marcaIndex) => {
          fieldIds.add(`packages_marcas_marca_${declarationIndex + 1}_${itemIndex}_${embalajeIndex}_${marcaIndex}`);
        });
      });
      
      // Document required fields
      item.documentos?.forEach((documento, docIndex) => {
        fieldIds.add(`documents_codigo_documento_${declarationIndex + 1}_${itemIndex}_${docIndex}`);
        fieldIds.add(`documents_tipo_${declarationIndex + 1}_${itemIndex}_${docIndex}`);
      });
    });
  });
  
  return fieldIds;
};

/**
 * Extract field information from validation errors - optimized version
 */
export const extractValidationErrorFields = (validationErrors) => {
  if (!validationErrors || !Array.isArray(validationErrors)) {
    return [];
  }
  
  const errors = [];
  
  // Helper to extract errors from an array
  const extractFromArray = (errorArray, type) => {
    if (!Array.isArray(errorArray)) return;
    for (let i = 0; i < errorArray.length; i++) {
      const error = errorArray[i];
      if (error?.field) {
        errors.push({ fieldId: error.field, errorLabel: error.error, type });
      }
    }
  };
  
  // Process all declarations
  for (let i = 0; i < validationErrors.length; i++) {
    const declaration = validationErrors[i];
    
    extractFromArray(declaration.declarationErrors, 'declaration');
    
    if (Array.isArray(declaration.itemErrors)) {
      for (let j = 0; j < declaration.itemErrors.length; j++) {
        extractFromArray(declaration.itemErrors[j].errors, 'item');
      }
    }
    
    if (Array.isArray(declaration.documentErrors)) {
      for (let j = 0; j < declaration.documentErrors.length; j++) {
        extractFromArray(declaration.documentErrors[j].errors, 'document');
      }
    }
    
    if (Array.isArray(declaration.packageErrors)) {
      for (let j = 0; j < declaration.packageErrors.length; j++) {
        extractFromArray(declaration.packageErrors[j].errors, 'package');
      }
    }
  }
  
  return errors;
};

/**
 * Create a field object with all necessary metadata
 */
export const createFieldObject = ({
  field,
  value,
  declarationIndex,
  itemIndex,
  docIndex,
  embajaIndex,
  marcaIndex,
  validationErrorFieldIds,
}) => {
  const fieldId = getFieldId(field, declarationIndex, itemIndex, docIndex, embajaIndex, marcaIndex);
  
  return {
    declarationIndex,
    itemIndex,
    docIndex,
    embajaIndex,
    marcaIndex,
    field,
    value,
    fieldId, // Pre-compute and cache fieldId to avoid recalculating
    hasValue: hasFieldValue(value),
    hasInput: hasInputElement(fieldId),
    isInValidationErrors: validationErrorFieldIds?.has(fieldId) || false,
  };
};

// Constants for excluded keys to avoid repeated array creation
const EXCLUDED_DECLARATION_KEYS = new Set(['partidas', 'documentos', 'embalajes']);
const EXCLUDED_ITEM_KEYS = new Set(['documentos', 'embalajes']);

/**
 * Organize fields from extracted data - optimized version
 */
export const organizeFieldsFromData = (extractedData, validationErrorFieldIds) => {
  if (!extractedData || (Array.isArray(extractedData) && extractedData.length === 0)) {
    return [];
  }
  
  const declarations = Array.isArray(extractedData) ? extractedData : [extractedData];
  
  return declarations.map((declaration, declIndex) => {
    // Declaration-level fields - use filter + map for better performance
    const declarationFields = Object.keys(declaration)
      .filter(key => !EXCLUDED_DECLARATION_KEYS.has(key))
      .map(key => createFieldObject({
        field: key,
        value: declaration[key],
        declarationIndex: declIndex,
        validationErrorFieldIds,
      }));
    
    // Item-level fields
    const itemFields = [];
    if (Array.isArray(declaration.partidas)) {
      for (let itemIndex = 0; itemIndex < declaration.partidas.length; itemIndex++) {
        const item = declaration.partidas[itemIndex];
        const itemFieldList = [];
        const documentFields = [];
        const packageFields = [];
        
        // Item fields - use for...in for better performance
        for (const key in item) {
          if (EXCLUDED_ITEM_KEYS.has(key)) continue;
          
          itemFieldList.push(createFieldObject({
            field: `partidas[${itemIndex}].${key}`,
            value: item[key],
            declarationIndex: declIndex,
            itemIndex,
            validationErrorFieldIds,
          }));
        }
        
        // Document fields
        if (Array.isArray(item.documentos)) {
          for (let docIndex = 0; docIndex < item.documentos.length; docIndex++) {
            const doc = item.documentos[docIndex];
            const docFieldList = Object.keys(doc).map(key => 
              createFieldObject({
                field: `documents_${key}`,
                value: doc[key],
                declarationIndex: declIndex,
                itemIndex,
                docIndex,
                validationErrorFieldIds,
              })
            );
            documentFields.push({ docIndex, fields: docFieldList });
          }
        }
        
        // Package fields
        if (Array.isArray(item.embalajes)) {
          for (let embajaIndex = 0; embajaIndex < item.embalajes.length; embajaIndex++) {
            const embalaje = item.embalajes[embajaIndex];
            const pkgFieldList = [];
            
            for (const key in embalaje) {
              if (key === 'marcas' && Array.isArray(embalaje.marcas)) {
                for (let marcaIndex = 0; marcaIndex < embalaje.marcas.length; marcaIndex++) {
                  pkgFieldList.push(createFieldObject({
                    field: 'packages_marcas_marca',
                    value: embalaje.marcas[marcaIndex].marca,
                    declarationIndex: declIndex,
                    itemIndex,
                    embajaIndex,
                    marcaIndex,
                    validationErrorFieldIds,
                  }));
                }
              } else if (key !== 'marcas') {
                pkgFieldList.push(createFieldObject({
                  field: `packages_${key}`,
                  value: embalaje[key],
                  declarationIndex: declIndex,
                  itemIndex,
                  embajaIndex,
                  validationErrorFieldIds,
                }));
              }
            }
            packageFields.push({ embajaIndex, fields: pkgFieldList });
          }
        }
        
        itemFields.push({
          itemIndex,
          fields: itemFieldList,
          documentFields,
          packageFields,
        });
      }
    }
    
    return {
      declarationIndex: declIndex,
      declarationFields,
      itemFields,
    };
  });
};

/**
 * Add missing fields from validation errors to organized fields
 */
export const addMissingFieldsFromErrors = (organizedFields, validationErrors, validationErrorFieldIds) => {
  if (!validationErrors || !Array.isArray(validationErrors)) {
    return organizedFields;
  }
  
  const errorFields = extractValidationErrorFields(validationErrors);
  
  errorFields.forEach(({ fieldId, errorLabel }) => {
    const normalizedId = normalizeValidationErrorFieldId(fieldId);
    const fieldInfo = parseFieldId(normalizedId) || parseFieldId(fieldId);
    if (!fieldInfo) return;
    
    const { type, fieldName, declarationIndex, itemIndex, docIndex, embajaIndex, marcaIndex } = fieldInfo;
    
    if (declarationIndex < 0 || declarationIndex >= organizedFields.length) return;
    
    const decl = organizedFields[declarationIndex];
    
    const addFieldToCollection = (collection, checkFn, fieldData) => {
      if (!collection.some(checkFn)) {
        collection.push(createFieldObject({
          ...fieldData,
          value: null,
          hasValue: false,
          validationErrorFieldIds,
        }));
      }
    };
    
    // Helper function to get or create item
    const getOrCreateItem = () => {
      let item = decl.itemFields.find(i => i?.itemIndex === itemIndex);
      if (!item) {
        item = { itemIndex, fields: [], documentFields: [], packageFields: [] };
        decl.itemFields.push(item);
      }
      return item;
    };
    
    switch (type) {
      case 'declaration':
        addFieldToCollection(
          decl.declarationFields,
          f => f.field === fieldName,
          { field: fieldName, declarationIndex }
        );
        break;
        
      case 'item': {
        const item = getOrCreateItem();
        addFieldToCollection(
          item.fields,
          f => f.field === `partidas[${itemIndex}].${fieldName}`,
          { field: `partidas[${itemIndex}].${fieldName}`, declarationIndex, itemIndex }
        );
        break;
      }
        
      case 'document': {
        const item = getOrCreateItem();
        if (!item.documentFields[docIndex]) {
          item.documentFields[docIndex] = { docIndex, fields: [] };
        }
        addFieldToCollection(
          item.documentFields[docIndex].fields,
          f => f.field === `documents_${fieldName}`,
          { field: `documents_${fieldName}`, declarationIndex, itemIndex, docIndex }
        );
        break;
      }
        
      case 'package':
      case 'package_marca': {
        const item = getOrCreateItem();
        if (!item.packageFields[embajaIndex]) {
          item.packageFields[embajaIndex] = { embajaIndex, fields: [] };
        }
        const pkg = item.packageFields[embajaIndex];
        if (type === 'package_marca') {
          addFieldToCollection(
            pkg.fields,
            f => f.field === 'packages_marcas_marca' && f.embajaIndex === embajaIndex && f.marcaIndex === marcaIndex,
            { field: 'packages_marcas_marca', declarationIndex, itemIndex, embajaIndex, marcaIndex }
          );
        } else {
          addFieldToCollection(
            pkg.fields,
            f => f.field === `packages_${fieldName}`,
            { field: `packages_${fieldName}`, declarationIndex, itemIndex, embajaIndex }
          );
        }
        break;
      }
    }
  });
  
  // Sort itemFields by itemIndex
  organizedFields.forEach(decl => {
    decl.itemFields = decl.itemFields
      .filter(item => item != null)
      .sort((a, b) => a.itemIndex - b.itemIndex);
  });
  
  return organizedFields;
};

